import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { deleteMedia, fetchMedia } from '../../api/mediaApi';
import { placeholderSvgDataUrl } from '../../utils/placeholderImage';

const AdminMedia = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('');
  const [query, setQuery] = useState('');

  const load = async (nextPage = page) => {
    setLoading(true); setErr('');
    try {
      const params = { page: nextPage, limit };
      if (type !== 'all') params.type = type;
      if (status !== 'all') params.status = status;
      if (category.trim()) params.category = category.trim();
      const data = await fetchMedia(params);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotalPages(Number(data?.totalPages || 1));
      setPage(Number(data?.page || nextPage));
    } catch (e) { setErr(e?.message || 'Failed to load media');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(1); }, [type, status, category, limit]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) => String(m.title || '').toLowerCase().includes(q));
  }, [items, query]);

  const onDelete = async (m) => {
    if (!window.confirm(`Delete media "${m.title}"?`)) return;
    try { await deleteMedia(m.id); await load(page); } catch (e) { window.alert(e?.message || 'Delete failed'); }
  };

  return (
    <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
      <style>{`
        .admin-media-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; flex-wrap: wrap; gap: 12px; }
        @media (max-width: 768px) {
          .admin-media-header { flex-direction: column; align-items: stretch; gap: 8px; }
          .admin-media-header h1 { font-size: 20px !important; }
          .admin-media-header p { font-size: 12px !important; margin-top: 2px !important; }
          .admin-media-header button { width: 100%; justify-content: center; }
        }
      `}</style>
      <div className="admin-media-header">
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#F9FAFB' }}>Media</h1>
          <p style={{ margin: '4px 0 0', color: '#9CA3AF', fontSize: '13px' }}>Upload and manage images & videos</p>
        </div>
        <button
          onClick={() => navigate('/admin/media/new')}
          style={{
            padding: '10px 20px', borderRadius: '8px', border: 'none',
            background: 'linear-gradient(135deg, #FFD700, #FFA500)',
            color: '#0B1020', fontWeight: 600, fontSize: '14px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '8px'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add Media
        </button>
      </div>

      <div style={{ background: '#0F1B2D', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)', padding: '16px 20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: '320px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by title..."
              style={{ width: '100%', padding: '9px 12px 9px 36px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#F3F4F6', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(255,215,0,0.4)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={type} onChange={(e) => setType(e.target.value)}
              style={{ padding: '9px 32px 9px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#F3F4F6', fontSize: '13px', outline: 'none', cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center' }}>
              <option value="all">All types</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              style={{ padding: '9px 32px 9px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#F3F4F6', fontSize: '13px', outline: 'none', cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center' }}>
              <option value="all">All status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category"
              style={{ padding: '9px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#F3F4F6', fontSize: '13px', outline: 'none', width: '140px' }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(255,215,0,0.4)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
            />
            <select value={String(limit)} onChange={(e) => setLimit(Number(e.target.value))}
              style={{ padding: '9px 32px 9px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#F3F4F6', fontSize: '13px', outline: 'none', cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center' }}>
              <option value="12">12 / page</option>
              <option value="24">24 / page</option>
              <option value="36">36 / page</option>
            </select>
            <button onClick={() => load(page)} disabled={loading}
              style={{ padding: '9px 16px', borderRadius: '8px', border: '1px solid rgba(255,215,0,0.2)', background: 'transparent', color: '#FFD700', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 500, opacity: loading ? 0.5 : 1 }}>
              Refresh
            </button>
          </div>
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
            <span style={{ color: '#6B7280', fontSize: '13px' }}>Page <strong style={{ color: '#FFD700' }}>{page}</strong> of <strong style={{ color: '#FFD700' }}>{totalPages}</strong></span>
            <div style={{ display: 'flex', gap: '6px' }}>
              {['First', 'Prev', 'Next'].map((label, idx) => {
                const acts = [() => load(1), () => load(page - 1), () => load(page + 1)];
                const dis = [page <= 1, page <= 1, page >= totalPages];
                return (
                  <button key={label} onClick={acts[idx]} disabled={loading || dis[idx]}
                    style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: dis[idx] ? '#4B5563' : '#D1D5DB', cursor: dis[idx] ? 'not-allowed' : 'pointer', fontSize: '12px', transition: 'all 0.15s' }}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {err && (
        <div style={{ padding: '14px 16px', background: 'rgba(239,68,68,0.1)', borderRadius: '10px', border: '1px solid rgba(239,68,68,0.2)', color: '#FCA5A5', fontSize: '14px', marginBottom: '20px' }}>
          {err}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>Loading...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px' }}>
          {filtered.length === 0 ? (
            <div style={{ gridColumn: '1/-1', padding: '40px', textAlign: 'center', color: '#6B7280' }}>No media found.</div>
          ) : (
            filtered.map((m) => {
              const preview = m.type === 'image' ? m.file_url : m.thumbnail || placeholderSvgDataUrl({ width: 640, height: 360, text: 'Video' });
              return (
                <div key={m.id} style={{ background: '#0F1B2D', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden', transition: 'all 0.25s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,215,0,0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.3)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ height: '180px', overflow: 'hidden', position: 'relative' }}>
                    <img src={preview} alt={m.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    {m.type === 'video' && (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(to top, rgba(0,0,0,0.5), rgba(0,0,0,0.2))' }}>
                        <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(255,215,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0B1020', fontWeight: 900, fontSize: '18px' }}>▶</div>
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'start' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 600, color: '#FFD700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '14px' }}>{m.title}</div>
                        <div style={{ color: '#9CA3AF', marginTop: '3px', fontSize: '12px' }}>
                          {m.type.toUpperCase()}{m.category ? ` • ${m.category}` : ''}
                        </div>
                      </div>
                      <span style={{ padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600, background: m.status === 'active' ? 'rgba(52,211,153,0.12)' : 'rgba(239,68,68,0.12)', color: m.status === 'active' ? '#34D399' : '#F87171', whiteSpace: 'nowrap' }}>
                        {m.status}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
                      <Link to={`/admin/media/${m.id}`} style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: 'rgba(255,215,0,0.12)', color: '#FFD700', cursor: 'pointer', fontWeight: 500, fontSize: '12px', textDecoration: 'none' }}>Edit</Link>
                      <Link to={`/media/${m.id}`} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#9CA3AF', cursor: 'pointer', fontWeight: 500, fontSize: '12px', textDecoration: 'none' }}>View</Link>
                      <button onClick={() => onDelete(m)} style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: 'rgba(239,68,68,0.1)', color: '#F87171', cursor: 'pointer', fontWeight: 500, fontSize: '12px', marginLeft: 'auto' }}>Delete</button>
                    </div>
                    {m.is_featured && (
                      <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '5px', color: '#FFD700', fontSize: '11px', fontWeight: 600 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="#FFD700" stroke="#FFD700" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                        Featured
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default AdminMedia;
